import { ChangeDetectionStrategy, Component, computed, inject, input, TemplateRef, viewChild } from '@angular/core';
// migrated 1:1 from @nexato/nx-core-module (#682)
import { Issue } from '../../../entities/issue/issue';
import { SidebarService } from '../../../services/sidebar/sidebar.service';

@Component({
  selector: 'div[issue-row], app-issue-row',
  templateUrl: './issue-row.component.html',
  styleUrls: ['./issue-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'class': 'block border-b border-gray first:border-t',
    '[class.last:border-b-0]': 'hideLastBorder()'
  }
})
export class IssueRowComponent {

  // Signal-based inputs
  readonly issue = input<Issue>();
  readonly isLast = input(false);
  readonly showBinaryStatus = input(false);
  readonly hideLastBorder = input(false);
  readonly displayedFields = input<string[]>([]);
  readonly showLabels = input<boolean>(true);

  // Injected services
  private readonly sidebarService = inject(SidebarService);

  // Whether this row is currently selected in the sidebar
  readonly isSelected = computed(() => 
    this.sidebarService.selectedEntityId() === this.issue()?.id
  );

  // Template references for field rendering
  protected readonly orderTpl = viewChild<TemplateRef<any>>('orderTpl');
  protected readonly contactTpl = viewChild<TemplateRef<any>>('contactTpl');
  protected readonly resourcesTpl = viewChild<TemplateRef<any>>('resourcesTpl');

  // Computed signals for derived state
  // TODO: These will be replaced with actual data from Issue entity once available
  protected readonly order = computed(() => {
    // Dummy data for now - will be replaced with: this.issue()?.order
    return { number: '26100-002', id: 'dummy-order-id' };
  });

  protected readonly contact = computed(() => {
    // Dummy data for now - will be replaced with: this.issue()?.contact
    return { text: 'Nexato GmbH', id: 'dummy-contact-id' };
  });

  protected readonly resources = computed(() => {
    // Dummy data for now - will be replaced with: this.issue()?.resources
    return [
      { text: 'Ressource 1', id: 'res-1' },
      { text: 'Ressource 2', id: 'res-2' }
    ];
  });

  // Filter displayedFields to only include fields with actual values
  protected readonly visibleFields = computed(() => {
    const issue = this.issue();
    const fields = this.displayedFields();
    
    return fields.filter(fieldName => {
      switch(fieldName) {
        case 'order':
          return !!this.order()?.number;
        case 'contact':
          return !!this.contact()?.id;
        case 'resources':
          return this.resources() && this.resources()!.length > 0;
        default:
          return false;
      }
    });
  });
  
  protected readonly showLabelsRow = computed(() => 
    this.showLabels() && this.issue()?.labels && this.issue()!.labels.length > 0
  );
  
  protected readonly showAdditionalInfoRow = computed(() => 
    this.visibleFields().length > 0
  );

  // Get template for a specific field
  getFieldTemplate(fieldName: string): TemplateRef<any> | null {
    switch(fieldName) {
      case 'order':
        return this.orderTpl() ?? null;
      case 'contact':
        return this.contactTpl() ?? null;
      case 'resources':
        return this.resourcesTpl() ?? null;
      default:
        return null;
    }
  }

}

